import baseAxiosInterceptor from "config/axios/base";
import qs from "qs";
import { useCallback, useLayoutEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { isAuthenticatedSelector, userSelector } from "store/auth";
import { globalsSelector } from "store/globals";
import { segmentsSelector } from "store/segments";
import {
	errorResponseInterceptor,
	getCurrentSubdomain,
	getPharmacySegmentFromSegments,
	isJustmedsTenant,
	successRequestInterceptor,
	successResponseInterceptor,
} from "utils/helpers";
import { useCollectContainer } from "utils/skyflowProxyContainers";

const useAxiosInterceptors = () => {
	const { i18n, t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { search } = useLocation();
	const { token: patient_intake_token = null } = qs.parse(search, { ignoreQueryPrefix: true });
	const skyFlowContainer = useCollectContainer();

	// redux global state
	const user = useSelector(userSelector);
	const isAuthenticated = useSelector(isAuthenticatedSelector);
	const globals = useSelector(globalsSelector);
	const segments = useSelector(segmentsSelector);

	// constants
	const pharmacySegment = useMemo(() => getPharmacySegmentFromSegments(segments), [segments]);
	const tenant = useMemo(() => getCurrentSubdomain(user?.tenant?.name || ""), [user?.tenant?.name]);
	const isCurrentSegmentPharmacySegment = pharmacySegment?.id === globals?.segment?.id || false;
	const isTenantJustmeds = isJustmedsTenant();

	// callback hooks
	const requestSuccessInterceptor = useCallback(
		function (config) {
			return successRequestInterceptor(config, {
				isAuthenticated,
				tenant,
				demo_token: globals?.demo_token,
				...(isTenantJustmeds && patient_intake_token && { patient_intake_token }),
			});
		},
		[globals?.demo_token, isAuthenticated, tenant, isTenantJustmeds, patient_intake_token]
	);
	const requestErrorInterceptor = useCallback(function (error) {
		return Promise.reject(error);
	}, []);

	const responseSuccessInterceptor = useCallback(
		function (response) {
			return successResponseInterceptor(response, {
				dispatch,
				demo_token: globals?.demo_token,
				navigate,
				language: i18n.language,
				t,
				skyFlowContainer,
			});
		},
		[dispatch, globals?.demo_token, i18n.language, navigate, t, skyFlowContainer]
	);
	const responseErrorInterceptor = useCallback(
		function (error) {
			return errorResponseInterceptor(error, {
				dispatch,
				navigate,
				language: i18n.language,
				t,
				isCurrentSegmentPharmacySegment,
				pharmacySegmentId: pharmacySegment?.id,
			});
		},
		[dispatch, i18n.language, isCurrentSegmentPharmacySegment, navigate, pharmacySegment?.id, t]
	);

	// effect hooks
	useLayoutEffect(() => {
		let baseAxiosReqInterceptor = baseAxiosInterceptor.interceptors.request.use(
			requestSuccessInterceptor,
			requestErrorInterceptor
		);

		return () => {
			baseAxiosInterceptor.interceptors.request.eject(baseAxiosReqInterceptor);
		};
	}, [requestSuccessInterceptor, requestErrorInterceptor]);

	useLayoutEffect(() => {
		let baseAxiosResInterceptor = baseAxiosInterceptor.interceptors.response.use(
			responseSuccessInterceptor,
			responseErrorInterceptor
		);

		return () => {
			baseAxiosInterceptor.interceptors.response.eject(baseAxiosResInterceptor);
		};
	}, [responseErrorInterceptor, responseSuccessInterceptor]);
};

export default useAxiosInterceptors;
